import { Stack, styled } from '@mui/material';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import { default as MuiDrawer } from '@mui/material/Drawer';
import { CSSObject, Theme } from '@mui/material/styles';
import { FC, ReactNode } from 'react';
import SidebarMenu from 'components/SidebarMenu';
import MenuLogo from 'components/SidebarMenu/MenuLogo';
import { useSidebar } from 'hooks/useSidebar';
import { dispatch } from 'store';
import { toggleSidebar } from 'store/Slices/SidebarSlice';
import useMobile from 'hooks/useMobile';
import MobileMenu from './MobileMenu';
import './Sidebar.scss';
import useAuth from 'store/Auth/useAuth';
import SuspenseLoader from 'components/SuspenseLoader';
import CustomerHeader from 'components/Header';
import SidebarBottomMenu from 'components/SidebarMenu/BottomMenu';

const drawerWidth = 240;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(12)} + 1px)`,
  },
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}));

interface Props {
  children?: ReactNode;
}

const SideBar: FC<Props> = ({ children }) => {
  const isSideBarOpen = useSidebar();
  const { isMobile } = useMobile();
  const { isCustomer, isLoading } = useAuth();

  const handleDrawerOpenClose = () => {
    dispatch(toggleSidebar());
  };

  if (isLoading) {
    return <SuspenseLoader />;
  }

  if (isMobile) {
    return (
      <div className="mobile-sidebar">
        <div className="mobile-sidebar-header">
          <MenuLogo isExpanded={false} />
          <MobileMenu />
        </div>
        <Box component="main" sx={{ flexGrow: 1, p: 1 }}>
          {children}
        </Box>
      </div>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      {isCustomer && <CustomerHeader />}
      <Box sx={{ display: 'flex' }}>
        <CssBaseline />
        <Drawer
          variant="permanent"
          open={isSideBarOpen}
          className="sidebar-menu"
        >
          <Stack className={isCustomer ? 'customer-menu-list' : 'menu-list'}>
            {!isCustomer && (
              <DrawerHeader
                style={{
                  alignSelf: 'start',
                  paddingLeft: '24px',
                  marginBottom: '16px',
                }}
              >
                <MenuLogo isExpanded={isSideBarOpen} />
              </DrawerHeader>
            )}

            <SidebarMenu isExpanded={isSideBarOpen} />
            <SidebarBottomMenu
              handleDrawerOpenClose={handleDrawerOpenClose}
              isExpanded={isSideBarOpen}
            />
          </Stack>
        </Drawer>
        <Box
          component="main"
          className={`main-content ${isCustomer ? 'customer-content' : ''}`}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default SideBar;
